import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Check, Sparkles, X } from 'lucide-react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';

import {
  dateKeyInTimeZone,
  dateStringToDate,
  formatDateTimeInTimeZone,
  timeZoneAbbrev,
} from '@/lib/date-values';
import {
  locationZoneOf,
  useScheduleTimezone,
  type ScheduleTimezone,
} from '@/lib/time/scheduleTimezone';
import { Button } from '@/components/atoms/button';
import { cn } from '@/lib/utils';

/**
 * One coalesced-offer pool member, in canonical assignment order (entry 0 is
 * the engine's pick), carrying that tech's OWN travel-in reasoning.
 */
export interface WizardTechPoolEntry {
  technicianId: string;
  technicianName: string;
  travelInMinutes?: number | null;
  travelOriginSource?: 'APPOINTMENT' | 'ROUTE_BASE' | 'UNKNOWN';
  travelOriginEndsAt?: string | null;
}

export interface AppointmentWizardSlot {
  slotId: string;
  startAt: string;
  endAt: string;
  /** §4.5 — customer-facing arrival window (UTC instants). */
  arrivalWindow?: { start: string; end: string };
  /** §4.5 — all qualified techs free at this start (coalesced offer). */
  techPool?: WizardTechPoolEntry[];
  /**
   * NOT-650 — the unfiltered coalesced offer, preserved when the technician
   * filter materializes a slot (which rewrites techPool down to the chosen
   * tech). Crew seeding reads this so a filtered pick can still staff a crew.
   */
  fullTechPool?: WizardTechPoolEntry[];
  timezone: string;
  /**
   * NOT-626 — zone of the service address when it differs from the company
   * `timezone`. Display metadata only (mirrors Appointment.siteTimezone);
   * `timezone` stays the canonical value persisted on create/reschedule.
   */
  siteTimezone?: string | null;
  technicianId: string;
  technicianName: string;
  crewTechnicianIds?: string[];
  crewTechnicianNames?: string[];
  score?: number;
  /** Canonical backend assignment order; lower is better. */
  assignmentRank?: number;
  /** §4.3 ranking signal surfaced as reasoning (NOT-553). */
  travelInMinutes?: number | null;
  /**
   * Anchor of the travel-in estimate: a same-day prior visit (the tech will
   * already be in the area) or the tech's configured route base. Belongs to
   * the slot's canonical technician, like `travelInMinutes`.
   */
  travelOriginSource?: 'APPOINTMENT' | 'ROUTE_BASE' | 'UNKNOWN';
  /**
   * When the travel anchor is an APPOINTMENT: that prior visit's end instant.
   * The anchor is the tech's LATEST same-day visit before this slot — it can
   * end hours earlier, so immediacy wording must check this, not assume it.
   */
  travelOriginEndsAt?: string | null;
  /** `'manual'` — the user picked the tech + time themselves (no slot search). */
  source?: 'manual';
}

interface AppointmentSlotPickerProps {
  slots: AppointmentWizardSlot[];
  selectedSlot?: AppointmentWizardSlot | null;
  selectedSlotId?: string | null;
  onSelect: (slot: AppointmentWizardSlot) => void;
  /**
   * Who is going, out of the people free at the chosen time — lead first.
   *
   * A coalesced slot is not one technician's opening, it is "these eight are
   * all free then". The picker used to take the best-ranked of them silently,
   * which is a fine default and a terrible only-option: the dispatcher knows
   * which of the eight owns that customer, which one has the part on the van,
   * and which two should go together.
   */
  selectedTechnicianIds?: string[];
  /** Set who is going. First id is the LEAD; the rest ride as helpers. */
  onSelectedTechniciansChange?: (slot: AppointmentWizardSlot, technicianIds: string[]) => void;
  /**
   * Open the crew editor — two people on one visit, a lead and a helper.
   *
   * The picker below assigns ONE technician: narrow to a person, take their
   * time, and they are the lead on it. A second pair of hands is a different
   * question (it changes which times exist at all, because both have to be
   * free), so it is answered by the crew editor — and this is the link to it,
   * next to the control someone is already looking at when they wonder.
   * Omitted when a crew is already set; the crew is then shown above.
   */
  onChooseCrew?: () => void;
  emptyReason?: string | null;
  /** Offered from the empty state — the answer to "nothing found". */
  onEnterManually?: () => void;
  /** Create-job quick search: lay the time-window slots out in a grid, not a stack. */
  dense?: boolean;
}

type TechnicianFilter = { id: string; name: string; count: number };

interface TimeWindowGroup {
  key: string;
  startAt: string;
  endAt: string;
  arrivalWindow?: { start: string; end: string };
  timezone: string;
  siteTimezone?: string | null;
  items: AppointmentWizardSlot[];
}

export function AppointmentSlotPicker({
  slots,
  selectedSlot,
  selectedSlotId,
  onSelect,
  emptyReason,
  onEnterManually,
  dense = false,
  onChooseCrew,
  selectedTechnicianIds,
  onSelectedTechniciansChange,
}: AppointmentSlotPickerProps) {
  const resolvedSelectedSlotId = selectedSlot?.slotId ?? selectedSlotId;
  // NOT-626 — render times on the internal schedule-display contract (#2079):
  // the user's company/browser preference, same as the job page and dispatch.
  // The engine annotates slots with the service-site zone; when it differs
  // from the display zone, an "at location" line disambiguates instead of the
  // whole picker silently flipping zones against the created visit.
  const scheduleTz = useScheduleTimezone();
  // Every slot in one search is for the SAME service address, so the site zone
  // is uniform across the list — resolve the picker's zone once. In `service`
  // mode this IS the site zone, which is what collapses the "on site" hints
  // below; in tenant/browser mode it resolves session-wide as before.
  const slotSiteZone = useMemo(
    () => slots.find((slot) => slot.siteTimezone)?.siteTimezone ?? null,
    [slots],
  );
  const pickerZone = scheduleTz.zoneFor(slotSiteZone);
  // NOT-553 — the availability engine's own ranking picks the "Best" card and
  // the spark on the day that holds it.
  const recommendedSlot = useMemo(() => {
    let best: AppointmentWizardSlot | null = null;
    for (const slot of slots) if (!best || compareSlotsByRank(slot, best) < 0) best = slot;
    return best;
  }, [slots]);
  const recommendedSlotId = recommendedSlot?.slotId ?? null;
  const earliestStartAt = useMemo(
    () =>
      slots.length
        ? slots.reduce((min, slot) => (slot.startAt < min ? slot.startAt : min), slots[0].startAt)
        : null,
    [slots],
  );
  const groups = useMemo(() => {
    const grouped = new Map<string, AppointmentWizardSlot[]>();
    for (const slot of slots) {
      // Day tabs bucket in the SAME zone the cards render in (the picker
      // zone — the record's zone in `service` mode), so a slot near midnight
      // never shows a time whose calendar day differs from the tab it sits
      // under.
      const day = dateKeyInTimeZone(slot.startAt, pickerZone);
      const items = grouped.get(day) ?? [];
      items.push(slot);
      grouped.set(day, items);
    }
    return Array.from(grouped.entries())
      .map(([day, items]) => ({ day, items }))
      .sort((a, b) => a.day.localeCompare(b.day));
  }, [slots, pickerZone]);
  const selectedSlotDay = useMemo(() => {
    if (!resolvedSelectedSlotId) return null;
    return (
      groups.find((group) => group.items.some((slot) => slot.slotId === resolvedSelectedSlotId))
        ?.day ?? null
    );
  }, [groups, resolvedSelectedSlotId]);
  const [activeDay, setActiveDay] = useState<string | null>(null);

  useEffect(() => {
    setActiveDay((current) => {
      if (groups.length === 0) return null;
      if (selectedSlotDay) return selectedSlotDay;
      if (current && groups.some((group) => group.day === current)) return current;
      return groups[0].day;
    });
  }, [groups, selectedSlotDay]);

  const activeGroup = groups.find((group) => group.day === activeDay) ?? groups[0];
  const technicianFilters = useMemo(
    () => (activeGroup ? getTechnicianFilters(activeGroup.items) : []),
    [activeGroup],
  );
  const [activeTechnicianId, setActiveTechnicianId] = useState('all');

  useEffect(() => {
    setActiveTechnicianId((current) => {
      if (current === 'all') return current;
      return technicianFilters.some((technician) => technician.id === current) ? current : 'all';
    });
  }, [technicianFilters]);

  const activeFilter = technicianFilters.find((technician) => technician.id === activeTechnicianId);
  const filteredActiveItems = useMemo(() => {
    if (!activeGroup) return [];
    if (activeTechnicianId === 'all') return activeGroup.items;
    return activeGroup.items
      .filter((slot) => slotMatchesTechnician(slot, activeTechnicianId))
      .map((slot) =>
        activeFilter
          ? materializeSlotForTechnician(slot, {
              technicianId: activeFilter.id,
              technicianName: activeFilter.name,
            })
          : slot,
      );
  }, [activeFilter, activeGroup, activeTechnicianId]);
  const activeTimeGroups = useMemo(
    () => groupSlotsByTimeWindow(filteredActiveItems),
    [filteredActiveItems],
  );
  if (slots.length === 0) {
    return (
      /*
       * The empty state says it once, and offers the way out.
       *
       * A banner at the top of the section used to say the same thing, so a
       * search that found nothing reported it twice — once in amber above the
       * controls, once here — and the button that actually resolves it was in
       * neither place. It belongs with the message it answers.
       */
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <p className="text-[13px] font-medium text-foreground">No times available in this range.</p>
        {emptyReason ? (
          <p className="max-w-md text-[12.5px] leading-[1.5] text-foreground/70">{emptyReason}</p>
        ) : null}
        {onEnterManually ? (
          <Button
            type="button"
            variant="outline"
            className="mt-1 h-8 px-3 text-[13px]"
            onClick={onEnterManually}
          >
            Enter a time manually
          </Button>
        ) : null}
      </div>
    );
  }

  const activeDate = dateStringToDate(activeGroup.day) ?? new Date(activeGroup.day);
  // The day's times, split so the recommendation can lead. It only leads on
  // the day that actually holds it; on any other day the strip's spark is what
  // points to it, rather than a card here pointing off-screen.
  const recommendedGroup =
    activeTimeGroups.find((group) =>
      group.items.some((slot) => slot.slotId === recommendedSlotId),
    ) ?? null;
  const otherTimeGroups = recommendedGroup
    ? activeTimeGroups.filter((group) => group.key !== recommendedGroup.key)
    : activeTimeGroups;

  return (
    <div className="space-y-2.5">
      {/* The best option is marked on its own card, and the day holding it is
          marked in the strip — no pinned banner. A separate "Best match" bar
          restated a time that was already in the grid, and on days that
          didn't hold it, it pointed away from everything on screen.

          Day, then technician, then time: three plain rows of choices with
          nothing drawn around them. The panel this replaces put a bordered box
          around a bordered strip around a ruled grid, so picking a time meant
          reading three nested frames first. */}
      {/*
       * A calendar strip: weekday over date over count, one fixed-width card
       * per day, scrolling on one line. Dates in a row of text pills were the
       * problem — every pill a different width, so nothing lined up and the
       * row wrapped onto a second line the moment the range grew. Equal cards
       * with the number as the anchor read as dates, and scroll instead of
       * reflowing the results below them.
       */}
      {/* The month the strip is showing — a bare "24" needs it, and it saves
          every card from repeating it. */}
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {format(activeDate, 'MMMM yyyy')}
      </p>
      <div className="-mx-0.5 flex items-stretch gap-1.5 overflow-x-auto px-0.5 pb-1">
        {groups.map((group) => {
          const dayDate = dateStringToDate(group.day) ?? new Date(group.day);
          const isActive = activeGroup.day === group.day;
          const hasSelectedSlot = group.items.some(
            (slot) => slot.slotId === resolvedSelectedSlotId,
          );
          const hasRecommended = group.items.some((slot) => slot.slotId === recommendedSlotId);
          return (
            <button
              key={group.day}
              type="button"
              aria-pressed={isActive}
              aria-label={`${format(dayDate, 'EEEE, MMMM d')} — ${group.items.length} times`}
              onClick={() => setActiveDay(group.day)}
              className={cn(
                'flex w-[64px] shrink-0 flex-col items-center gap-0.5 rounded-xl border px-1 py-2 transition-colors',
                isActive
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border bg-card text-foreground hover:border-foreground/30 hover:bg-muted/50',
              )}
            >
              <span
                className={cn(
                  'text-[10px] font-medium uppercase tracking-wide',
                  isActive ? 'opacity-70' : 'text-muted-foreground',
                )}
              >
                {format(dayDate, 'EEE')}
              </span>
              <span className="text-[17px] font-semibold leading-none tracking-tight">
                {format(dayDate, 'd')}
              </span>
              {/* A mark, not a count: how many openings a day has is not how
                  you choose a day, and the number under every card turned the
                  strip into a table of figures. The times themselves are one
                  click away. */}
              <span
                className={cn(
                  'flex h-3 items-center text-[10.5px] leading-none',
                  isActive ? 'opacity-70' : 'text-muted-foreground',
                )}
              >
                {hasSelectedSlot ? (
                  <Check className="size-3" />
                ) : hasRecommended ? (
                  <Sparkles className="size-3" />
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      {technicianFilters.length > 1 || scheduleTz.hasDisplayChoice ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {technicianFilters.length > 1 ? (
            /*
             * One control, whatever the headcount.
             *
             * This was a run of names with a bare number after each — "All 8
             * Bob Marley 1 Harry Hilton 5 …". Nothing said they were people
             * you could filter by, nothing said what the number counted, and
             * at ten technicians it wrapped into a wall of text above the
             * times it was meant to narrow. A shop with forty made it
             * unreadable.
             *
             * A named select says all three things at once: whose times these
             * are, that you can change it, and — in the menu — how many each
             * of them has. It costs one line at any size of shop.
             */
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="shrink-0 text-[12px] text-muted-foreground">Technician</span>
              <Select
                value={activeTechnicianId}
                onValueChange={(next) => {
                  setActiveTechnicianId(next);
                  /*
                   * Narrowing to a person is a statement about WHO, not just
                   * about what is on screen — so if a time is already picked
                   * and they are free at it, they take the lead on it. Without
                   * this the header said one name and the visit was filed
                   * under another, which is the kind of disagreement nobody
                   * notices until the wrong van turns up.
                   */
                  if (next === 'all' || !selectedSlot || !onSelectedTechniciansChange) return;
                  const pool = selectedSlot.fullTechPool ?? selectedSlot.techPool ?? [];
                  if (!pool.some((technician) => technician.technicianId === next)) return;
                  const current = selectedTechnicianIds?.length
                    ? selectedTechnicianIds
                    : [selectedSlot.technicianId];
                  if (current.length === 1 && current[0] === next) return;
                  /*
                   * The filter REPLACES whoever was going. Always.
                   *
                   * Narrowing to a name means "this visit is hers" — so she
                   * leads it, alone, and any helpers are picked again against
                   * that decision. Promoting her over the existing crew was
                   * the other option and it is worse in the case that matters:
                   * three names were chosen to go with a lead who is no longer
                   * leading, and keeping them silently sends a crew nobody
                   * assembled for this. Re-picking two helpers costs two taps;
                   * a van sent for nothing costs a morning.
                   */
                  onSelectedTechniciansChange(selectedSlot, [next]);
                }}
              >
                <SelectTrigger className="h-8 w-auto min-w-[11rem] max-w-[16rem] text-[13px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    Anyone
                    <span className="text-muted-foreground">
                      {' '}
                      · {timesLabel(activeGroup.items.length)}
                    </span>
                  </SelectItem>
                  {technicianFilters.map((technician) => (
                    <SelectItem key={technician.id} value={technician.id}>
                      {technician.name}
                      <span className="text-muted-foreground">
                        {' '}
                        · {timesLabel(technician.count)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {onChooseCrew ? (
                <button
                  type="button"
                  onClick={onChooseCrew}
                  className="shrink-0 text-[12px] font-medium text-foreground underline decoration-dotted underline-offset-4 hover:decoration-solid"
                >
                  Add a helper
                </button>
              ) : null}
            </div>
          ) : (
            <span className="flex-1" />
          )}
          {/* Which clock the times below are on. It belongs over the times it
              qualifies, not up on the day chips. */}
          {scheduleTz.hasDisplayChoice ? (
            <p className="shrink-0 text-[11px] font-medium text-foreground/75">
              {scheduleTz.mode === 'tenant'
                ? 'Company'
                : scheduleTz.mode === 'service'
                  ? 'Service'
                  : 'My'}{' '}
              time ({timeZoneAbbrev(pickerZone, activeDate)})
            </p>
          ) : null}
        </div>
      ) : null}

      {/*
       * The times themselves: one card each, on a grid. A time is a small,
       * uniform thing to choose between — a grid of equal cards is the shape
       * that makes twelve of them scannable, where full-width rows made each
       * one look like a different amount of content. Where several technicians
       * are free at the same time the card says so and takes the best-ranked;
       * naming a specific one is what the technician filter above is for.
       */}
      {/* The recommendation leads. Sitting in clock order it was just one card
          among twelve wearing a badge — a recommendation nobody reads first is
          not doing its job. Lifted out under its own caption it reads as the
          answer, and the rest stay available underneath in the order a clock
          puts them. */}
      {recommendedGroup ? (
        <div className="space-y-1.5">
          <p className="text-[11px] font-medium text-muted-foreground">Recommended</p>
          <div className={cn('grid gap-2', dense ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2')}>
            <SlotTimeCard
              group={recommendedGroup}
              scheduleTz={scheduleTz}
              selectedSlot={selectedSlot}
              selectedSlotId={selectedSlotId}
              earliestStartAt={earliestStartAt}
              onSelect={onSelect}
            />
          </div>
        </div>
      ) : null}
      <div className="space-y-1.5">
        {recommendedGroup && otherTimeGroups.length > 0 ? (
          <p className="text-[11px] font-medium text-muted-foreground">Other times</p>
        ) : null}
        <div className={cn('grid gap-2', dense ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2')}>
          {activeTimeGroups.length > 0 ? (
            otherTimeGroups.map((group) => (
              <SlotTimeCard
                key={group.key}
                group={group}
                scheduleTz={scheduleTz}
                selectedSlot={selectedSlot}
                selectedSlotId={selectedSlotId}
                earliestStartAt={earliestStartAt}
                onSelect={onSelect}
              />
            ))
          ) : (
            <p className="col-span-full py-6 text-center text-sm text-muted-foreground">
              No times for this technician.
            </p>
          )}
        </div>
      </div>

      {onSelectedTechniciansChange ? (
        <CrewFromSlot
          slot={selectedSlot ?? null}
          scheduleTz={scheduleTz}
          selectedTechnicianIds={selectedTechnicianIds ?? []}
          onChange={onSelectedTechniciansChange}
        />
      ) : null}

      {onEnterManually ? (
        <div className="flex justify-end border-t border-border pt-2.5">
          <Button
            type="button"
            variant="outline"
            className="h-8 px-3 text-[13px]"
            onClick={onEnterManually}
          >
            Enter a time manually
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Who is going, out of the people free at the time that was picked.
 *
 * Appears only once a time is chosen, and only when that time has more than
 * one technician behind it — the question does not exist before the first and
 * has one answer after the second. It is the last thing on the section
 * because it is the last decision: a time, then the people for it.
 *
 * The lead is a separate control rather than "whoever you tapped first".
 * Order-of-tapping is invisible once the taps are done, and the lead is the
 * name the customer is told and the one the visit is filed under — too load-
 * bearing to be inferred from a gesture nobody can see afterwards.
 */
function CrewFromSlot({
  slot,
  scheduleTz,
  selectedTechnicianIds,
  onChange,
}: {
  slot: AppointmentWizardSlot | null;
  scheduleTz: ScheduleTimezone;
  selectedTechnicianIds: string[];
  onChange: (slot: AppointmentWizardSlot, technicianIds: string[]) => void;
}) {
  // `fullTechPool` is the offer before the technician filter narrowed it
  // (NOT-650) — the whole crew is pickable even while the grid above shows one
  // person's times.
  const pool = slot ? (slot.fullTechPool ?? slot.techPool ?? []) : [];
  if (!slot || pool.length < 2) return null;

  const chosen = selectedTechnicianIds.length ? selectedTechnicianIds : [slot.technicianId];
  const leadId = chosen[0];
  const nameOf = (id: string) =>
    pool.find((technician) => technician.technicianId === id)?.technicianName ?? 'Technician';

  /**
   * Tapping a name that is already going makes them the LEAD.
   *
   * Not "remove" — that was the obvious mapping and the wrong one. The lead is
   * the name the customer is told and the one the visit is filed under, so
   * changing it is the thing a dispatcher does most often once the crew is
   * right; removing someone they deliberately added is rare, and it gets the ×
   * rather than the whole target.
   */
  const choose = (technicianId: string) => {
    if (!chosen.includes(technicianId)) {
      // New names ride as helpers. Whoever was leading keeps leading — adding
      // a second pair of hands is not a change of who owns the job.
      onChange(slot, [...chosen, technicianId]);
      return;
    }
    if (technicianId === leadId) return;
    onChange(slot, [technicianId, ...chosen.filter((id) => id !== technicianId)]);
  };

  const drop = (technicianId: string) => {
    // Never nobody: someone has to go, and the last one standing is who.
    if (chosen.length === 1) return;
    onChange(
      slot,
      chosen.filter((id) => id !== technicianId),
    );
  };

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <p className="text-[13px] font-medium">Who's going</p>
        <p className="text-[12px] text-muted-foreground">
          {scheduleTz.format(
            slot.startAt,
            { hour: 'numeric', minute: '2-digit' },
            slot.siteTimezone,
          )}{' '}
          · {pool.length} free at this time
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {pool.map((technician) => {
          const id = technician.technicianId;
          const active = chosen.includes(id);
          const isLead = active && id === leadId;
          return (
            <span
              key={id}
              className={cn(
                'inline-flex h-8 items-center rounded-full border text-[12px] transition-colors',
                active
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border bg-card text-foreground hover:bg-muted/60',
              )}
            >
              <button
                type="button"
                aria-pressed={active}
                title={active && !isLead ? 'Make lead' : undefined}
                onClick={() => choose(id)}
                className={cn(
                  'inline-flex h-8 items-center gap-1.5 pl-3',
                  active ? 'pr-2' : 'pr-3',
                )}
              >
                {isLead ? <Check className="size-3 shrink-0" aria-hidden /> : null}
                <span className={cn(active && 'font-medium')}>{technician.technicianName}</span>
                {isLead ? (
                  <span className="rounded-full bg-background/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    Lead
                  </span>
                ) : null}
              </button>
              {active && chosen.length > 1 ? (
                <button
                  type="button"
                  aria-label={`Take ${technician.technicianName} off this visit`}
                  onClick={() => drop(id)}
                  className="inline-flex h-8 items-center pr-2.5 text-background/70 hover:text-background"
                >
                  <X className="size-3" aria-hidden />
                </button>
              ) : null}
            </span>
          );
        })}
      </div>

      <p className="text-[12px] text-muted-foreground">
        {chosen.length > 1
          ? `${nameOf(leadId)} leads · ${chosen.length - 1 === 1 ? '1 helper' : `${chosen.length - 1} helpers`} · tap a name to make them lead`
          : 'Tap another name to send two people on this visit.'}
      </p>
    </div>
  );
}

/** "5 times" — the count in the technician menu, said rather than implied. */
function timesLabel(count: number): string {
  return `${count} ${count === 1 ? 'time' : 'times'}`;
}

/**
 * One offered time, as a card on a grid. Start–end reads first; the technician
 * (or how many are free at that start) sits under it. Selecting fills the card
 * dark — the same "chosen" language the rest of the form uses.
 */
function SlotTimeCard({
  group,
  scheduleTz,
  selectedSlot,
  selectedSlotId,
  earliestStartAt,
  onSelect,
}: {
  group: TimeWindowGroup;
  scheduleTz: ScheduleTimezone;
  selectedSlot?: AppointmentWizardSlot | null;
  selectedSlotId?: string | null;
  earliestStartAt: string | null;
  onSelect: (slot: AppointmentWizardSlot) => void;
}) {
  // Several technicians free at this start → take the best-ranked. Naming one
  // is what the technician filter above the grid is for, so the card does not
  // need to become a list of its own.
  const best = [...group.items].sort(compareSlotsByRank)[0];
  // `isSlotOfferSelected`, not `isSlotSelected`: picking a helper out of the
  // pool rewrites the chosen slot's technician, and a strict id match then
  // stopped calling this card selected while it plainly was.
  const isSelected = group.items.some((slot) =>
    isSlotOfferSelected(slot, selectedSlot, selectedSlotId),
  );
  // Why the engine likes this time — a prior visit nearby, or the start of a
  // route day. Kept from main: the card should say what it knows.
  const proximityHint = getSlotProximityHint(best, scheduleTz);
  // Site zone when annotated, else the slot's company timezone — the hint
  // must survive a site that shares the company zone (see the picker header).
  const locationZone = locationZoneOf(group.siteTimezone, group.timezone);
  // `formatPlain`, not `format`: the picker names its clock once above the grid
  // ("Company time (CDT)"), and `format` appends the abbreviation to every time
  // whose zone differs from the viewer's — so each card read "2:00 PM CDT –
  // 4:00 PM CDT" under a heading already saying CDT.
  const startLabel = scheduleTz.formatPlain(
    group.startAt,
    { hour: 'numeric', minute: '2-digit' },
    group.siteTimezone,
  );
  const endLabel = scheduleTz.formatPlain(
    group.endAt,
    { hour: 'numeric', minute: '2-digit' },
    group.siteTimezone,
  );
  // Resolved against THIS record's zone, so in `service` mode the hint
  // collapses on its own — the primary time already IS the site's time.
  const onSiteLabel = scheduleTz.differsFromDisplay(locationZone, group.siteTimezone)
    ? `${formatDateTimeInTimeZone(group.startAt, locationZone, {
        hour: 'numeric',
        minute: '2-digit',
      })} on site`
    : null;

  return (
    <button
      type="button"
      onClick={() => onSelect(best)}
      aria-pressed={isSelected}
      title={[onSiteLabel, ...buildSlotReasons(best, earliestStartAt, scheduleTz)]
        .filter(Boolean)
        .join(' · ')}
      className={cn(
        'flex flex-col items-start gap-0.5 rounded-lg border px-3 py-2.5 text-left transition-colors',
        isSelected
          ? 'border-foreground bg-foreground text-background'
          : 'border-border bg-card hover:border-foreground/30 hover:bg-muted/50',
      )}
    >
      <span className="flex w-full items-center gap-1.5">
        <span
          data-testid="slot-time"
          className="min-w-0 flex-1 truncate text-[13.5px] font-medium leading-tight"
        >
          {startLabel} - {endLabel}
        </span>
      </span>
      {/* NOT-626 — when the site sits in another zone than the one these times
          are printed in, the site-local hour rides the same line. It is the
          only thing on the card that stops a cross-zone job being booked at
          the wrong hour, so it stays visible rather than living in a title. */}
      <span
        className={cn(
          'w-full truncate text-[11px] leading-tight',
          isSelected ? 'opacity-70' : 'text-muted-foreground',
        )}
      >
        {getSlotTechnicianLabel(best)}
        {onSiteLabel ? ` · ${onSiteLabel}` : ''}
      </span>
      {/* Why the engine ranks this time where it does — a prior visit nearby,
          or the head of a route day. Its own line, not tacked onto the one
          above: it is a sentence, and the only thing on the card the agent
          could not work out for themselves. */}
      {proximityHint ? (
        <span
          className={cn(
            'w-full truncate text-[11px] leading-tight',
            isSelected ? 'opacity-70' : 'text-muted-foreground',
          )}
        >
          {proximityHint}
        </span>
      ) : null}
    </button>
  );
}

function groupSlotsByTimeWindow(slots: AppointmentWizardSlot[]) {
  const grouped = new Map<string, TimeWindowGroup>();

  for (const slot of slots) {
    const key = [
      slot.startAt,
      slot.endAt,
      slot.arrivalWindow?.start ?? '',
      slot.arrivalWindow?.end ?? '',
    ].join('|');
    const group = grouped.get(key) ?? {
      key,
      startAt: slot.startAt,
      endAt: slot.endAt,
      arrivalWindow: slot.arrivalWindow,
      timezone: slot.timezone,
      siteTimezone: slot.siteTimezone,
      items: [],
    };
    group.items.push(slot);
    grouped.set(key, group);
  }

  return Array.from(grouped.values())
    .map((group) => ({
      ...group,
      items: [...group.items].sort((a, b) =>
        getSlotTechnicianLabel(a).localeCompare(getSlotTechnicianLabel(b)),
      ),
    }))
    .sort((a, b) => a.startAt.localeCompare(b.startAt));
}

function getTechnicianFilters(slots: AppointmentWizardSlot[]) {
  const technicians = new Map<string, TechnicianFilter>();

  function addTechnician(id: string, name: string) {
    const current = technicians.get(id);
    technicians.set(id, {
      id,
      name,
      count: (current?.count ?? 0) + 1,
    });
  }

  for (const slot of slots) {
    if (getSlotCrewLabel(slot)) continue;
    if (slot.techPool && slot.techPool.length > 1) {
      slot.techPool.forEach((technician) =>
        addTechnician(technician.technicianId, technician.technicianName),
      );
      continue;
    }
    addTechnician(slot.technicianId, slot.technicianName);
  }

  return Array.from(technicians.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function slotMatchesTechnician(slot: AppointmentWizardSlot, technicianId: string) {
  if (slot.techPool?.some((technician) => technician.technicianId === technicianId)) return true;
  return slot.technicianId === technicianId;
}

function materializeSlotForTechnician(
  slot: AppointmentWizardSlot,
  technician: { technicianId: string; technicianName: string },
): AppointmentWizardSlot {
  const entry = slot.techPool?.find(
    (candidate) => candidate.technicianId === technician.technicianId,
  );
  if (!entry) return slot;
  return {
    ...slot,
    technicianId: entry.technicianId,
    technicianName: entry.technicianName,
    techPool: [entry],
    // Keep the full coalesced offer so crew seeding (NOT-650) still sees every
    // tech free at this start — techPool above is display-narrowed only.
    fullTechPool: slot.fullTechPool ?? slot.techPool,
    // Adopt the chosen tech's OWN travel reasoning — the slot-level fields
    // described the engine's canonical pick, not every tech in the pool.
    travelInMinutes: entry.travelInMinutes ?? null,
    travelOriginSource: entry.travelOriginSource,
    travelOriginEndsAt: entry.travelOriginEndsAt ?? null,
  };
}

// The engine's #rank/Recommended signals belong to the offer's canonical tech.
// A slot the technician filter materialized for another pool member reuses the
// same slotId, so those badges must be suppressed on it — otherwise filtering
// to Bob shows Alice's "#1" on Bob's row.
/**
 * The offer, rewritten for the technician actually chosen out of its pool.
 *
 * A coalesced offer carries per-technician travel reasoning, and the slot's
 * own copy belongs to the engine's canonical pick — so reassigning without
 * these left the booking quoting a different technician's drive than the one
 * going. `fullTechPool` is preserved either way: crew seeding reads it after
 * the technician filter has narrowed `techPool` (NOT-650).
 */
export function assignSlotToTechnician(
  slot: AppointmentWizardSlot,
  technicianId: string,
): AppointmentWizardSlot {
  const pool = slot.fullTechPool ?? slot.techPool ?? [];
  const entry = pool.find((technician) => technician.technicianId === technicianId);
  return {
    ...slot,
    fullTechPool: slot.fullTechPool ?? slot.techPool,
    technicianId,
    technicianName: entry?.technicianName ?? slot.technicianName,
    travelInMinutes: entry?.travelInMinutes ?? null,
    travelOriginSource: entry?.travelOriginSource,
    travelOriginEndsAt: entry?.travelOriginEndsAt ?? null,
  };
}

function isCanonicalPick(slot: AppointmentWizardSlot) {
  const canonicalTechnicianId =
    slot.fullTechPool?.[0]?.technicianId ?? slot.techPool?.[0]?.technicianId ?? slot.technicianId;
  return slot.technicianId === canonicalTechnicianId;
}

// An offer counts as selected when the chosen (slot, tech) pair came from it —
// either its canonical tech (isSlotSelected) or any member of its pool (a pick
// made from the expanded technician list or the day's technician filter).
function isSlotOfferSelected(
  slot: AppointmentWizardSlot,
  selectedSlot?: AppointmentWizardSlot | null,
  selectedSlotId?: string | null,
) {
  if (isSlotSelected(slot, selectedSlot, selectedSlotId)) return true;
  return Boolean(
    selectedSlot &&
    selectedSlot.slotId === slot.slotId &&
    slot.techPool?.some((tech) => tech.technicianId === selectedSlot.technicianId),
  );
}

function isSlotSelected(
  slot: AppointmentWizardSlot,
  selectedSlot?: AppointmentWizardSlot | null,
  selectedSlotId?: string | null,
) {
  if (selectedSlot) {
    return slot.slotId === selectedSlot.slotId && slot.technicianId === selectedSlot.technicianId;
  }
  return Boolean(selectedSlotId && slot.slotId === selectedSlotId);
}

function getSlotCrewLabel(slot: AppointmentWizardSlot) {
  return slot.crewTechnicianNames && slot.crewTechnicianNames.length > 1
    ? `Crew: ${slot.crewTechnicianNames.join(' + ')}`
    : null;
}

function getSlotTechnicianLabel(slot: AppointmentWizardSlot) {
  const crewLabel = getSlotCrewLabel(slot);
  if (crewLabel) return crewLabel;
  // "2 techs", not "2 techs available" — it sits on a button inside a list of
  // available times, so "available" is the one word it never needs to say, and
  // the extra width was pushing every card onto a second line.
  return slot.techPool && slot.techPool.length > 1
    ? `${slot.techPool.length} techs`
    : slot.technicianName;
}

// NOT-553 — one canonical ordering drives BOTH the rank number shown on every
// slot and the Recommended badge (#1). Mirrors the availability engine's order:
// assignmentRank ascending (lower = better), then higher score as the tiebreak
// the shared slot sorter uses. Crew slots (no rank) fall back to score.
function compareSlotsByRank(a: AppointmentWizardSlot, b: AppointmentWizardSlot): number {
  const aRank = a.assignmentRank;
  const bRank = b.assignmentRank;
  if (aRank != null && bRank != null && aRank !== bRank) return aRank - bRank;
  if (aRank != null && bRank == null) return -1;
  if (aRank == null && bRank != null) return 1;
  return (b.score ?? 0) - (a.score ?? 0);
}

// Factual, engine-aligned reasons for the popover. The rank ("#1 of N") is added
// by the caller; these are the human-readable signals behind that rank.
function buildSlotReasons(
  slot: AppointmentWizardSlot,
  earliestStartAt: string | null,
  scheduleTz: ScheduleTimezone,
): string[] {
  const reasons: string[] = [];
  if (earliestStartAt && slot.startAt === earliestStartAt) reasons.push('Soonest available time');
  const proximity = getSlotProximityHint(slot, scheduleTz);
  if (proximity) reasons.push(proximity);
  return reasons;
}

// Facts behind one technician's travel-in estimate — a slot's canonical tech
// or any coalesced-pool member.
interface ProximityFacts {
  technicianName: string;
  travelInMinutes?: number | null;
  travelOriginSource?: 'APPOINTMENT' | 'ROUTE_BASE' | 'UNKNOWN';
  travelOriginEndsAt?: string | null;
}

// The APPOINTMENT anchor is the tech's LATEST prior visit that day, which can
// end hours before the slot — every afternoon slot then carries the same
// morning anchor. "Just before this time" is only true within this window;
// past it, name the time the prior visit ends instead.
const PRIOR_VISIT_IMMEDIACY_MINUTES = 75;

// Name the technician and justify their travel estimate, so the dispatcher can
// see who the engine would send and why — e.g. they finish a prior visit near
// the job shortly before the slot.
function buildProximityHint(
  facts: ProximityFacts,
  slotStartAt: string,
  scheduleTz: ScheduleTimezone,
  // The slot's service-site zone: the ends-at clock rides the same card as the
  // slot times, so it must resolve on the same per-record contract.
  siteTimezone?: string | null,
): string | null {
  if (facts.travelInMinutes == null) return null;
  const minutes = Math.max(1, Math.round(facts.travelInMinutes));
  if (facts.travelOriginSource === 'APPOINTMENT') {
    const endsAtMs = facts.travelOriginEndsAt ? Date.parse(facts.travelOriginEndsAt) : NaN;
    const gapMinutes = Number.isFinite(endsAtMs)
      ? (Date.parse(slotStartAt) - endsAtMs) / 60_000
      : null;
    if (gapMinutes != null && gapMinutes <= PRIOR_VISIT_IMMEDIACY_MINUTES) {
      return `${facts.technicianName} finishes a prior visit ~${minutes} min away just before this time`;
    }
    if (gapMinutes != null) {
      const endsLabel = scheduleTz.formatPlain(
        facts.travelOriginEndsAt!,
        { hour: 'numeric', minute: '2-digit' },
        siteTimezone,
      );
      return `${facts.technicianName} finishes a prior visit ~${minutes} min away at ${endsLabel}`;
    }
    // Older payload without the anchor's end time: claim no immediacy.
    return `${facts.technicianName} comes from a prior visit ~${minutes} min away`;
  }
  if (facts.travelOriginSource === 'ROUTE_BASE') {
    return `${facts.technicianName} starts their route ~${minutes} min from this job`;
  }
  // Unknown/absent anchor (e.g. an older payload): claim only the distance.
  return `${facts.technicianName} is ~${minutes} min away`;
}

function getSlotProximityHint(
  slot: AppointmentWizardSlot,
  scheduleTz: ScheduleTimezone,
): string | null {
  return buildProximityHint(slot, slot.startAt, scheduleTz, slot.siteTimezone);
}
